import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import ReactDOMServer from "react-dom/server";
import { Box, Typography, Divider, Grid2, Checkbox } from "@mui/material";
import SearchInput from "../../Overview/components/SearchInput";
import FleetRangeDistributionTooltip from "./FleetRangeDistributionTooltip";
import BreakdownChart from "../../BmsFaults/Analytics/BreakdownChart";
import RenderChartLegend from "../../AdvancedAnalytics/ContinuousChart/RenderChartLegend";
import { formatDateRange } from "../../../Helper/Analytics/AnalyticsHelper";
import {
  GroupByButton,
  DropdownMenu,
  GroupByMenuItem,
} from "../../BmsFaults/Components/StyledComponents";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import FleetRangeDistributionDrillDownModal from "./ChartDrillDown/FleetRangeDistributionDrillDownModal";
import { formatDateRangeWithSmartYear } from "../../../Helper/DatePicker/DateFormatters";
import {
  getFleetRangeDistributionLevel1TableConfig,
  getFleetRangeDistributionLevel1Data,
  getFleetRangeDistributionLevel2TableConfig,
  getFleetRangeDistributionLevel2Data,
} from "./ChartDrillDown/ChartDrillDownTable";
import CartLoader from "../../../Helper/Loaders/CartLoader";
import { useFleetBreakdownData } from "../Hooks/useFleetBreakdownData";
import { getFleetPerformanceAnalyticsList } from "../../../Api/FleetPerformanceApi";
import {
  FLEET_PERFORMANCE_CHART_KEYS,
  resolveFleetGroupByField,
  useFleetBreakdownDimensions,
} from "../fleetBreakdownDimensions";

// ─── Chart identity ───────────────────────────────────────────────────────────
const CHART_ID = "fleet-range-distribution-chart";

// ─── Colours for chart data
const STATE_COLORS = ["#006882", "#84C1CC", "#DA5F00"];

const LEGEND_SERIES = [
  { name: "Excellent", color: STATE_COLORS[0] },
  { name: "Good", color: STATE_COLORS[1] },
  { name: "Poor", color: STATE_COLORS[2] },
];

// ─── Temporary sidebar data (commented out — replaced by API) ─────────────────
// const SIDEBAR_BY_GROUPBY = {
//   originCity: [
//     { id: "all", label: "All", count: 524 },
//     { id: "bangalore", label: "Bangalore", count: 106 },
//     { id: "mangalore", label: "Mangalore", count: 58 },
//     { id: "mumbai", label: "Mumbai", count: 58 },
//     { id: "surat", label: "Surat", count: 58 },
//     { id: "delhi", label: "Delhi", count: 58 },
//     { id: "city", label: "City", count: 58 },
//   ],
//   originState: [
//     { id: "all", label: "All", count: 524 },
//     { id: "karnataka", label: "Karnataka", count: 164 },
//     { id: "maharashtra", label: "Maharashtra", count: 116 },
//     { id: "gujarat", label: "Gujarat", count: 116 },
//     { id: "delhi_state", label: "Delhi", count: 128 },
//   ],
//   originDepot: [
//     { id: "all", label: "All", count: 524 },
//     { id: "depot1", label: "Depot 1", count: 87 },
//     { id: "depot2", label: "Depot 2", count: 75 },
//     { id: "depot3", label: "Depot 3", count: 92 },
//     { id: "depot4", label: "Depot 4", count: 63 },
//     { id: "depot5", label: "Depot 5", count: 45 },
//   ],
//   configName: [
//     { id: "all", label: "All", count: 524 },
//     { id: "mahindra", label: "Mahindra", count: 222 },
//     { id: "tata", label: "Tata", count: 174 },
//     { id: "byd", label: "BYD", count: 96 },
//     { id: "mg", label: "MG", count: 32 },
//   ],
//   cellChemistry: [
//     { id: "all", label: "All", count: 524 },
//     { id: "nmc", label: "NMC", count: 312 },
//     { id: "lfp", label: "LFP", count: 212 },
//   ],
//   hierarchy: [
//     { id: "all", label: "All", count: 524 },
//     { id: "fleet1", label: "Fleet 1", count: 175 },
//     { id: "fleet2", label: "Fleet 2", count: 156 },
//     { id: "fleet3", label: "Fleet 3", count: 193 },
//   ],
// };

// ─── Temporary chart rows (commented out — replaced by API) ───────────────────
// const CHART_ROWS = [
//   { project: "314 kWh",  numberOfVehicles: 25, excellent: 15, good: 8, poor: 5,  certified_range: 660, ranges: { excellent: { min: 594, max: 660 }, good: { min: 462, max: 594 }, poor: { min: 0, max: 462 } } },
//   { project: "112 kWh",  numberOfVehicles: 19, excellent: 10, good: 5, poor: 4,  certified_range: 570, ranges: { excellent: { min: 513, max: 570 }, good: { min: 399, max: 513 }, poor: { min: 0, max: 399 } } },
//   { project: "2210 kWh", numberOfVehicles: 28, excellent: 18, good: 7, poor: 3,  certified_range: 450, ranges: { excellent: { min: 405, max: 450 }, good: { min: 315, max: 405 }, poor: { min: 0, max: 315 } } },
//   { project: "891 kWh",  numberOfVehicles: 15, excellent: 8,  good: 3, poor: 4,  certified_range: 300, ranges: { excellent: { min: 270, max: 300 }, good: { min: 210, max: 270 }, poor: { min: 0, max: 210 } } },
//   { project: "4510 kWh", numberOfVehicles: 12, excellent: 7,  good: 2, poor: 3,  certified_range: 720, ranges: { excellent: { min: 648, max: 720 }, good: { min: 504, max: 648 }, poor: { min: 0, max: 504 } } },
//   { project: "891 kWh",  numberOfVehicles: 9,  excellent: 5,  good: 3, poor: 1,  certified_range: 540, ranges: { excellent: { min: 486, max: 540 }, good: { min: 378, max: 486 }, poor: { min: 0, max: 378 } } },
//   { project: "4510 kWh", numberOfVehicles: 18, excellent: 11, good: 4, poor: 3,  certified_range: 470, ranges: { excellent: { min: 423, max: 470 }, good: { min: 329, max: 423 }, poor: { min: 0, max: 329 } } },
// ];

// ─── Component ────────────────────────────────────────────────────────────────
const FleetRangeDistributionChart = ({ startDate, endDate, globalFilters }) => {
  // ─── Menu, from FleetPerformance org meta where the org configures it ────────
  const groupByOptions = useFleetBreakdownDimensions(
    FLEET_PERFORMANCE_CHART_KEYS.FLEET_RANGE_DISTRIBUTION
  );

  const [groupBy, setGroupBy] = useState("originCity");
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);

  // Drill-down modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDataPoint, setSelectedDataPoint] = useState(null);

  // ─── API: fetch sidebar (checkbox list) — same logic as Energy Consumption ──
  const handleFetchSidebarData = useCallback(async (currentGroupBy) => {
    // The dimension's own value is the backend field; see resolveFleetGroupByField
    const backendGroupBy = resolveFleetGroupByField(currentGroupBy);
    try {
      const response = await getFleetPerformanceAnalyticsList(globalFilters, {
        groupBy: [backendGroupBy],
        select:  ["DistinctVehicleCount"],
        startDate,
        endDate,
        sortBy: JSON.stringify([{ column: "DistinctVehicleCount", direction: "DESC" }]),
      });
      if (response?.responseStatus?.code === 200) {
        const data  = response.response?.data || [];
        const items = data.map((item) => ({
          id:    item.xLabel,
          label: item.xLabel,
          count: (item.metricsArray || []).reduce((s, m) => s + (m.value || 0), 0),
        }));
        const totalCount = items.reduce((s, i) => s + (i.count || 0), 0);
        return { items: [{ id: "all", label: "All", count: totalCount }, ...items] };
      }
    } catch (err) {
      console.error("FleetRangeDistribution: sidebar fetch error", err);
    }
    return { items: [] };
  }, [startDate, endDate, globalFilters]);

  // ─── API: fetch chart data filtered by selected sidebar items ───────────────
  const handleFetchChartData = useCallback(async (currentGroupBy, currentSelectedItems) => {
    // The dimension's own value is the backend field; see resolveFleetGroupByField
    const backendGroupBy = resolveFleetGroupByField(currentGroupBy);
    const isAllSelected  = currentSelectedItems.some((i) => i.id === "all");
    const filters        = (!isAllSelected && currentSelectedItems.length > 0)
      ? [{ name: backendGroupBy, values: currentSelectedItems.map((i) => i.id) }]
      : [];
    try {
      const response = await getFleetPerformanceAnalyticsList(globalFilters, {
        groupBy: ["ProjectName", "RangeBucket"],
        select:  ["DistinctVehicleCount"],
        startDate,
        endDate,
        ...(filters.length > 0 && { filters }),
        sortBy: JSON.stringify([{ column: "DistinctVehicleCount", direction: "DESC" }]),
      });
      if (response?.responseStatus?.code === 200) {
        const data = response.response?.data || [];
        return data.map((item) => {
          const metrics   = item.metricsArray || [];
          const getValue  = (type) => metrics.find((m) => m.type === type)?.value ?? 0;
          const excellent = getValue("Excellent");
          const good      = getValue("Good");
          const poor      = getValue("Poor");
          return {
            project:          item.xLabel,
            excellent,
            good,
            poor,
            numberOfVehicles: excellent + good + poor,
            certified_range:  null,  // not available from analytics API
            ranges:           null,  // not available from analytics API
          };
        });
      }
    } catch (err) {
      console.error("FleetRangeDistribution: chart fetch error", err);
    }
    return [];
  }, [startDate, endDate, globalFilters]);

  const config = useMemo(() => ({
    onFetchSidebarData: handleFetchSidebarData,
    onFetchChartData:   handleFetchChartData,
  }), [handleFetchSidebarData, handleFetchChartData]);

  // The built-in default is originCity, but an org can drop it and the page meta
  // arrives after the first render. Move to the first dimension on the menu once
  // it does, rather than grouping by something the menu no longer offers.
  useEffect(() => {
    if (!groupByOptions.length) return;
    if (groupByOptions.some((option) => option.value === groupBy)) return;
    setGroupBy(groupByOptions[0].value);
    setSearchQuery("");
  }, [groupByOptions, groupBy]);

  // ─── Hook — same hook used by FleetBreakdownChart (Energy Consumption) ──────
  const {
    groupByItems,
    selectedItems,
    setSelectedItems,
    chartData,
    loadingGroupBy,
    loadingChart,
  } = useFleetBreakdownData({ dependencies: [startDate, endDate, globalFilters], groupBy, config });

  // chartData from hook replaces the hardcoded CHART_ROWS
  const chartRows = chartData;

  const handleGroupByChange = (value) => {
    setGroupBy(value);
    setSearchQuery("");
    setDropdownAnchorEl(null);
    // selectedItems reset is handled automatically by the hook when groupBy changes
  };

  const handleItemToggle = useCallback((item) => {
    if (loadingChart) return;
    setSelectedItems((prev) => {
      const isAllSelected = prev.some((i) => i.id === "all");
      let next = [];
      if (item.id === "all") {
        next = isAllSelected ? [] : [item];
      } else {
        if (isAllSelected) {
          next = groupByItems.filter((i) => i.id !== "all" && i.id !== item.id);
        } else {
          next = prev.some((i) => i.id === item.id)
            ? prev.filter((i) => i.id !== item.id)
            : [...prev, item];
        }
      }
      const allIndividual = groupByItems.filter((i) => i.id !== "all");
      if (next.length === allIndividual.length && !next.some((i) => i.id === "all")) {
        const allItem = groupByItems.find((i) => i.id === "all");
        if (allItem) return [allItem];
      }
      return next;
    });
  }, [groupByItems, loadingChart, setSelectedItems]);

  const filteredItems = groupByItems.filter((item) =>
    item.label?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Keep a ref to the latest click-relevant state so the stable event handler
  // always reads fresh values without being re-created on every render.
  const clickStateRef = useRef({ groupBy, selectedItems });
  useEffect(() => {
    clickStateRef.current = { groupBy, selectedItems };
  }, [groupBy, selectedItems]);

  // Stable handler — passed to ApexCharts dataPointSelection event
  const handleDataPointSelection = useCallback((event, chartContext, config) => {
    const dataPointIndex = config?.dataPointIndex;
    if (dataPointIndex === undefined || dataPointIndex < 0) return;

    const row = chartRows[dataPointIndex];
    if (!row) return;

    const { groupBy: currentGroupBy, selectedItems: currentSelectedItems } = clickStateRef.current;
    const option   = groupByOptions.find((o) => o.value === currentGroupBy);
    const labelStr = currentSelectedItems.some((i) => i.id === "all")
      ? "All"
      : currentSelectedItems.map((i) => i.label).join(", ");

    setSelectedDataPoint({
      project:           row.project,
      groupByLabel:      option?.label || "By City",
      selectedItemLabel: labelStr || "All",
    });
    setModalOpen(true);
  }, [chartRows, groupByOptions]);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedDataPoint(null);
  }, []);

  const categories = useMemo(() => chartRows.map((r) => r.project), [chartRows]);

  const series = useMemo(() => [
    { name: "Excellent", data: chartRows.map((r) => r.excellent) },
    { name: "Good",      data: chartRows.map((r) => r.good) },
    { name: "Poor",      data: chartRows.map((r) => r.poor) },
  ], [chartRows]);

  const chartOptions = useMemo(() => {
    return {
      chart: {
        id: CHART_ID,
        stacked: true,
        events: {
          dataPointSelection: handleDataPointSelection,
        },
      },
      colors: STATE_COLORS,
      xaxis: { categories },
      legend: { show: false },
      plotOptions: {
        bar: {
          dataLabels: {
            total: {
              enabled: true,
              formatter: (_value, opts) => {
                return chartRows[opts.dataPointIndex]?.numberOfVehicles ?? "";
              },
              style: {
                fontSize: "12px",
                fontWeight: 500,
                color: "#494949",
              },
              offsetX: 10,
            },
          },
        },
      },
      states: {
        active: {
          filter: { type: "none" },
        },
      },
      tooltip: {
        custom: ({ dataPointIndex }) => {
          const row = chartRows[dataPointIndex];
          if (!row) return "";

          return ReactDOMServer.renderToStaticMarkup(
            <FleetRangeDistributionTooltip
              project={row.project}
              series={[
                {
                  label: "Excellent",
                  color: STATE_COLORS[0],
                  value: row.excellent,
                  min: row.ranges?.excellent?.min,
                  max: row.ranges?.excellent?.max,
                },
                {
                  label: "Good",
                  color: STATE_COLORS[1],
                  value: row.good,
                  min: row.ranges?.good?.min,
                  max: row.ranges?.good?.max,
                },
                {
                  label: "Poor",
                  color: STATE_COLORS[2],
                  value: row.poor,
                  min: row.ranges?.poor?.min,
                  max: row.ranges?.poor?.max,
                },
              ]}
              certifiedRange={row.certified_range}
            />
          );
        },
      },
    };
  }, [categories, chartRows, handleDataPointSelection]);

  const selectedOption = groupByOptions.find((o) => o.value === groupBy);

  // Build modal title JSX
  const buildModalTitle = useCallback((dataPoint) => {
    const groupByLabel      = dataPoint?.groupByLabel      || "By City";
    const selectedItemLabel = dataPoint?.selectedItemLabel || "All";
    const project           = dataPoint?.project           || "";
    const dateRange         = formatDateRangeWithSmartYear(startDate, endDate);

    return (
      <Grid2 container gap={1} alignItems="center">
        <Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "28px" }}>
          {`Fleet Range Distribution: ${groupByLabel} / ${selectedItemLabel} / `}
          <Typography component="span" variant="fw500s16c000000" sx={{ lineHeight: "28px" }}>
            {project}
          </Typography>
        </Typography>
        <Typography variant="fw500s14c686868" sx={{ lineHeight: "20px" }}>{"•"}</Typography>
        <Typography variant="fw500s14c686868" sx={{ lineHeight: "20px" }}>{dateRange}</Typography>
      </Grid2>
    );
  }, [startDate, endDate]);

  // Build level 2 title (shown in the back-navigation header)
  const buildLevel2Title = useCallback((level2Record) => {
    const city = level2Record?.city || "Vehicles";
    return `${city} / Details`;
  }, []);

  // Memoised table configs
  const level1TableConfig = useMemo(() => getFleetRangeDistributionLevel1TableConfig(), []);
  const level2TableConfig = useMemo(() => getFleetRangeDistributionLevel2TableConfig(), []);

  return (
    <>
      <Box sx={{ bgcolor: "#fff", borderRadius: "8px", border: "1px solid #E6E6E6", overflow: "hidden", maxWidth: "calc(100vw - 102px)" }}>

        {/* ── Header ──────────────────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: "28px", py: "24px" }}>
          <Typography variant="fw600s16lh24c444B51" sx={{ color: "#2E2E2E" }}>
            Fleet Range Distribution
          </Typography>
          <Typography variant="fw400s12c686868" sx={{ lineHeight: "20px" }}>
            {formatDateRange(startDate, endDate)}
          </Typography>
        </Box>

        {/* ── Body ────────────────────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", overflowX: "auto" }}>

          {/* ── Left sidebar ────────────────────────────────────────────────── */}
          <Box
            sx={{
              width: "340px",
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              mb: 2,
            }}
          >
            <Box sx={{ padding: "0 16px" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <GroupByButton
                  onClick={(e) => setDropdownAnchorEl(e.currentTarget)}
                  endIcon={<ArrowDropDownIcon sx={{ color: "#5E758D", fontSize: "10px" }} />}
                  startIcon={<GridViewRoundedIcon sx={{ color: "#5E758D", fontSize: "12px" }} />}
                >
                  {selectedOption?.label}
                </GroupByButton>
                <Typography variant="fw400s12c686868">NO. OF VEHICLES</Typography>
              </Box>

              <Divider sx={{ mb: 1, borderColor: "#E4E4E4" }} />

              <Box sx={{ mb: 1 }}>
                <SearchInput
                  placeholder={`Search ${selectedOption?.label?.replace("By ", "") || "City"}`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </Box>

              <DropdownMenu
                anchorEl={dropdownAnchorEl}
                open={Boolean(dropdownAnchorEl)}
                onClose={() => setDropdownAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              >
                {groupByOptions.map((option) => (
                  <GroupByMenuItem
                    key={option.value}
                    data-testid={`fleet-range-group-by-${option.value}`}
                    selected={groupBy === option.value}
                    onClick={() => handleGroupByChange(option.value)}
                  >
                    {option.label}
                  </GroupByMenuItem>
                ))}
              </DropdownMenu>
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", padding: "0px 20px 0px 28px" }}>
              {loadingGroupBy ? (
                <Box sx={{ padding: "16px", display: "flex", justifyContent: "center" }}>
                  <CartLoader />
                </Box>
              ) : filteredItems.length === 0 ? (
                <Box sx={{ padding: "16px", textAlign: "center" }}>
                  <Typography variant="fw400s14c686868">No items found</Typography>
                </Box>
              ) : (
                <Grid2 container gap={0.5} flexDirection="column">
                  {filteredItems.map((item) => (
                    <Box
                      key={item.id}
                      onClick={() => handleItemToggle(item)}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 0",
                        cursor: "pointer",
                        "&:hover": { opacity: 0.8 },
                        transition: "opacity 0.2s",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Checkbox
                          checked={selectedItems.some((i) => i.id === "all") || selectedItems.some((i) => i.id === item.id)}
                          indeterminate={item.id === "all" && !selectedItems.some((i) => i.id === "all") && selectedItems.length > 0}
                          sx={{
                            padding: 0,
                            color: "#6D72F6",
                            "&.Mui-checked": { color: "#6D72F6" },
                            "&.MuiCheckbox-indeterminate": { color: "#6D72F6" },
                            "& .MuiSvgIcon-root": { fontSize: "20px" },
                          }}
                        />
                        <Typography
                          variant="fw400s14c000000"
                          sx={{ color: "#202124", fontSize: "14px", fontWeight: 500 }}
                        >
                          {item.label}
                        </Typography>
                      </Box>
                      <Typography variant="fw600s14c000000" sx={{ color: "#202124", fontSize: "14px", fontWeight: 500 }}>
                        {item.count}
                      </Typography>
                    </Box>
                  ))}
                </Grid2>
              )}
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ borderColor: "#E4E4E4", mb: 6 }} />

          {/* ── Right chart section ─────────────────────────────────────────── */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              padding: "24px 28px",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {/* "PROJECT" label left + legend centred in remaining space */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#8F8F8F",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  flexShrink: 0,
                }}
              >
                Project
              </Typography>
              <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
                <RenderChartLegend
                  legendSeries={LEGEND_SERIES}
                  chartIDs={[CHART_ID]}
                  isLoading={false}
                  centered={true}
                />
              </Box>
            </Box>

            {/* Stacked bar chart */}
            {loadingChart ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
                <CartLoader />
              </Box>
            ) : selectedItems.length === 0 || chartRows.length === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
                <Typography variant="fw400s14c686868">
                  {selectedItems.length === 0 ? "Select at least one item" : "No chart data available"}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ flex: 1, overflow: "hidden", pr: 2 }}>
                <BreakdownChart
                  series={series}
                  categories={categories}
                  options={chartOptions}
                  minVisibilityHeight={200}
                />
              </Box>
            )}

            {/* X-axis label */}
            <Box sx={{ display: "flex", justifyContent: "center", mt: 0.5 }}>
              <Typography sx={{ fontSize: "12px", color: "#8F8F8F" }}>
                Number Of Vehicles
              </Typography>
            </Box>
          </Box>

        </Box>
      </Box>

      {/* ── Drill-down modal ────────────────────────────────────────────────── */}
      <FleetRangeDistributionDrillDownModal
        open={modalOpen}
        onClose={handleModalClose}
        title={buildModalTitle}
        selectedDataPoint={selectedDataPoint}
        tableConfigs={level1TableConfig}
        getTableData={getFleetRangeDistributionLevel1Data}
        level2TableConfigs={level2TableConfig}
        level2GetTableData={getFleetRangeDistributionLevel2Data}
        level2Title={buildLevel2Title}
      />
    </>
  );
};

export default FleetRangeDistributionChart;
